// Maps chat-message media records onto the shape `MediaPreview` expects.
// Kept out of the view so the mediaType → preview-type rules live in one place.

import { MEDIA_PREVIEW_TYPE } from "../../../commonComponents/MediaPreview";
import { heicToJpg } from "../../../../utils";

// `mediaType` is a MIME-ish string ("image/jpeg", "image/gif", "video/mp4",
// "application/pdf"), so we match on substrings rather than an enum.
export const getPreviewType = mediaType => {
  const type = String(mediaType || "").toLowerCase();
  if (type.includes("video")) return MEDIA_PREVIEW_TYPE.VIDEO;
  if (type.includes("application")) return MEDIA_PREVIEW_TYPE.DOC;
  return MEDIA_PREVIEW_TYPE.IMAGE;
};

// Filename out of a media URL, for the previewer's title and the doc iframe.
export const getMediaFileName = url => {
  if (!url) return "";
  try {
    return decodeURIComponent(url.substring(url.lastIndexOf("/") + 1));
  } catch {
    return "";
  }
};

/**
 * Builds the previewer's slide list from a message's media array.
 *
 * Images keep using `compressedMediaUrl` when present — the same source the
 * thumbnail already renders, so the previewer doesn't start pulling heavier
 * originals than before. `heicToJpg` is applied here so iPhone HEIC uploads
 * display, matching the thumbnail path.
 *
 * `downloadUrl` stays the original `mediaUrl`: a download should give the full
 * asset even when the preview showed a compressed one.
 */
/**
 * Builds the previewer's slide list from composer attachments — files picked but
 * not yet sent, so each carries a local `preview` blob URL and the original
 * `File`. No `downloadUrl`: the file isn't on the server yet, so callers pass
 * `showDownload={false}` (mediaDownloader proxies through the API and can't take
 * a blob: URL).
 */
export const buildAttachmentItems = attachments =>
  (Array.isArray(attachments) ? attachments : [])
    .filter(attachment => attachment?.preview)
    .map(attachment => ({
      url: attachment.preview,
      type: getPreviewType(attachment?.file?.type),
      name: attachment?.file?.name || "",
      mediaType: attachment?.file?.type,
    }));

export const buildMessageMediaItems = media =>
  (Array.isArray(media) ? media : [])
    .filter(item => item?.mediaUrl || item?.compressedMediaUrl)
    .map(item => {
      const type = getPreviewType(item?.mediaType);
      const source = item?.compressedMediaUrl || item?.mediaUrl;
      return {
        url: type === MEDIA_PREVIEW_TYPE.IMAGE ? heicToJpg(source) : source,
        type,
        name: getMediaFileName(item?.mediaUrl || source),
        // Carried through for the download handler, not used by MediaPreview.
        downloadUrl: item?.mediaUrl || source,
        mediaType: item?.mediaType,
      };
    });
